import { useCallback, useMemo } from 'react'
import {
  type AccountFilterData,
  type AccountMapInput,
  type AreaData,
  type ElasticFilter,
  type ElasticFiltersInput,
  type GeoHashGridData,
  isEmpty,
  isNotDefined,
  isNotEmpty,
} from '@terros/common'
import { useQueryClient, type QueryClient, type QueryKey } from '@tanstack/react-query'
import { US_BOUNDING_BOX, useElasticFilters } from '../search/elasticFilter'
import { type GetHookResponse } from '../hooks'
import { accountMapQueryKey, useAccountMap } from '../account/map'

export type AreaAccountCounts = {
  total: number
  assigned: number
  unassigned: number
}

export type AccountCountSearchResult =
  | { mode: 'account'; data: Pick<AccountFilterData, 'assigned'>[]; total: number }
  | {
      mode: 'grid'
      data: Pick<GeoHashGridData, 'assignedCount' | 'count' | 'unassignedCount'>[]
      total: number
    }

export const useSearchAccountCounts = (elastic: ElasticFilter, enabled = true): GetHookResponse<AreaAccountCounts> => {
  const input: ElasticFiltersInput = useMemo(
    () => ({ filters: { ...elastic, boundingBox: toFilterBoundingBox(elastic) } }),
    [elastic]
  )
  const { data, ...state } = useElasticFilters(input, enabled)
  const counts = useMemo(() => (isNotDefined(data) ? undefined : toAreaAccountCounts(data)), [data])

  return {
    ...state,
    data: counts,
  }
}

type AreaAccountTotalSource = Pick<AreaData, 'coordinates' | 'filters'>

export const toAreaAccountTotalFilter = (area: AreaAccountTotalSource): ElasticFilter => ({
  coordinates: isNotEmpty(area.coordinates) ? area.coordinates : undefined,
  ...area.filters,
})

const toAccountMapTotalInput = (elastic: ElasticFilter): AccountMapInput => ({
  filters: elastic,
  mode: 'heatmap',
})

export const areaAccountTotalQueryKey = (area: AreaAccountTotalSource): QueryKey =>
  accountMapQueryKey(toAccountMapTotalInput(toAreaAccountTotalFilter(area)))

export const invalidateAreaAccountTotal = (queryClient: QueryClient, area: AreaAccountTotalSource): Promise<void> =>
  queryClient.invalidateQueries({
    queryKey: areaAccountTotalQueryKey(area),
    exact: true,
  })

export const useInvalidateAreaAccountTotal = (): ((area: AreaAccountTotalSource) => void) => {
  const queryClient = useQueryClient()
  return useCallback(
    (area: AreaAccountTotalSource): void => {
      void invalidateAreaAccountTotal(queryClient, area)
    },
    [queryClient]
  )
}

export const useAccountMapTotal = (elastic: ElasticFilter, enabled = true): GetHookResponse<number> => {
  const input = useMemo(() => toAccountMapTotalInput(elastic), [elastic])
  const { data, ...state } = useAccountMap(input, enabled)

  return {
    ...state,
    data: data?.total,
  }
}

export const toAreaAccountCounts = (result: AccountCountSearchResult): AreaAccountCounts => {
  if (result.mode === 'account') {
    const assigned = result.data.filter((account) => account.assigned).length
    const unassigned = result.data.filter((account) => !account.assigned).length
    return { total: result.total, assigned, unassigned }
  }

  if (isEmpty(result.data)) return { total: result.total, assigned: 0, unassigned: 0 }
  return result.data.reduce<AreaAccountCounts>(
    (counts, bucket) => ({
      total: counts.total + bucket.count,
      assigned: counts.assigned + bucket.assignedCount,
      unassigned: counts.unassigned + bucket.unassignedCount,
    }),
    { total: 0, assigned: 0, unassigned: 0 }
  )
}

export const toFilterBoundingBox = (
  elastic: Pick<ElasticFilter, 'boundingBox' | 'coordinates'>
): ElasticFilter['boundingBox'] => {
  if (elastic.boundingBox) return elastic.boundingBox
  if (isNotEmpty(elastic.coordinates)) {
    const { minLat, maxLat, minLng, maxLng } = elastic.coordinates.reduce(
      (acc, curr) => {
        return {
          minLat: Math.min(acc.minLat, curr.latitude),
          maxLat: Math.max(acc.maxLat, curr.latitude),
          minLng: Math.min(acc.minLng, curr.longitude),
          maxLng: Math.max(acc.maxLng, curr.longitude),
        }
      },
      { minLat: Infinity, maxLat: -Infinity, minLng: Infinity, maxLng: -Infinity }
    )
    return {
      top: { latitude: maxLat, longitude: minLng },
      bottom: { latitude: minLat, longitude: maxLng },
    }
  }
  return US_BOUNDING_BOX
}
